import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Filter, PackagePlus, Plus, ScanBarcode } from 'lucide-react'
import { z } from 'zod'

import type {
    ListNotesParams,
    NoteStatus,
    ProductLotInReason,
} from '@/lib/api/product-lot-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import {
    NOTE_CARD_CODE_CLASS,
    NoteListCard,
} from '@/components/common/NoteListCard'
import { RefreshButton } from '@/components/common/refresh-button'
import { ProductLotNotePageHeader } from '@/components/product-lots/ProductLotNotePageHeader'
import { ProductLotStatusBadge } from '@/components/product-lots/ProductLotStatusBadge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    useProductLotInNotesInfiniteQuery,
    useProductLotInNotesQuery,
} from '@/hooks/use-product-lot-query'
import { formatDateTimeVN } from '@/lib/format'
import {
    LIST_SEARCH_KEYS,
    useRememberListSearch,
} from '@/lib/list-search-store'
import { m } from '@/lib/paraglide/messages'

const searchSchema = z.object({
    status: z.enum(['pending', 'approved', 'rejected', 'cancelled']).optional(),
    reason: z
        .enum([
            'from_packaging',
            'from_customer_return',
            'adjustment',
            'from_manufacturing_partner',
        ])
        .optional(),
    from_date: z.string().optional(),
    to_date: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
})

export type Search = z.infer<typeof searchSchema>

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/stock-in-notes/',
)({
    component: Page,
    validateSearch: searchSchema,
})

function Page() {
    const search = Route.useSearch()
    useRememberListSearch(LIST_SEARCH_KEYS.stockInNotes, search)
    const navigate = useNavigate({
        from: '/sales-warehouse/stock-in-notes/',
    })
    const setSearch = (next: Search) => {
        void navigate({ search: () => next, replace: false })
    }
    const setPage = (p: number) =>
        setSearch({ ...search, page: p > 1 ? p : undefined })

    const [filterDialogOpen, setFilterDialogOpen] = useState(false)

    const params = useMemo<ListNotesParams>(
        () => ({
            page: search.page ?? 1,
            per_page: 50,
            ...(search.status ? { status: search.status } : {}),
            ...(search.reason ? { reason: search.reason } : {}),
            ...(search.from_date ? { date_from: search.from_date } : {}),
            ...(search.to_date ? { date_to: search.to_date } : {}),
        }),
        [search],
    )

    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } = useProductLotInNotesQuery(
        params,
        { enabled: !isMobile },
    )

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({
            per_page: 50,
            ...(search.status ? { status: search.status } : {}),
            ...(search.reason ? { reason: search.reason } : {}),
            ...(search.from_date ? { date_from: search.from_date } : {}),
            ...(search.to_date ? { date_to: search.to_date } : {}),
        }),
        [search.status, search.reason, search.from_date, search.to_date],
    )
    const infinite = useProductLotInNotesInfiniteQuery(infiniteParams, {
        enabled: isMobile,
    })
    const mobileNotes = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const notes = data?.data ?? []
    const total = data?.meta?.total ?? 0
    const page = search.page ?? 1

    const filterActive =
        !!search.status ||
        !!search.reason ||
        !!search.from_date ||
        !!search.to_date ||
        page > 1
    const clearFilter = () => setSearch({})

    const renderFilters = () => (
        <>
            <div>
                <Label className="mb-1 block">
                    {m.sl_note_filter_status()}
                </Label>
                <Select
                    value={search.status ?? 'all'}
                    onValueChange={(v) =>
                        setSearch({
                            ...search,
                            status: v === 'all' ? undefined : (v as NoteStatus),
                            page: undefined,
                        })
                    }
                >
                    <SelectTrigger className="w-full">
                        <SelectValue
                            placeholder={m.sl_note_filter_all_status()}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.sl_note_filter_all_status()}
                        </SelectItem>
                        <SelectItem value="pending">
                            {m.sl_note_status_pending()}
                        </SelectItem>
                        <SelectItem value="approved">
                            {m.sl_note_status_approved()}
                        </SelectItem>
                        <SelectItem value="rejected">
                            {m.sl_note_status_rejected()}
                        </SelectItem>
                        <SelectItem value="cancelled">
                            {m.sl_note_status_cancelled()}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label className="mb-1 block">
                    {m.sl_note_filter_reason()}
                </Label>
                <Select
                    value={search.reason ?? 'all'}
                    onValueChange={(v) =>
                        setSearch({
                            ...search,
                            reason:
                                v === 'all'
                                    ? undefined
                                    : (v as ProductLotInReason),
                            page: undefined,
                        })
                    }
                >
                    <SelectTrigger className="w-full">
                        <SelectValue
                            placeholder={m.sl_note_filter_all_reasons()}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.sl_note_filter_all_reasons()}
                        </SelectItem>
                        <SelectItem value="from_packaging">
                            {m.pll_in_reason_from_packaging()}
                        </SelectItem>
                        <SelectItem value="from_customer_return">
                            {m.pll_in_reason_from_customer_return()}
                        </SelectItem>
                        <SelectItem value="adjustment">
                            {m.pll_in_reason_adjustment()}
                        </SelectItem>
                        <SelectItem value="from_manufacturing_partner">
                            {m.pll_in_reason_from_manufacturing_partner()}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div>
                <Label className="mb-1 block">
                    {m.sl_note_filter_from_date()}
                </Label>
                <Input
                    type="date"
                    value={search.from_date ?? ''}
                    onChange={(e) =>
                        setSearch({
                            ...search,
                            from_date: e.target.value || undefined,
                            page: undefined,
                        })
                    }
                />
            </div>
            <div>
                <Label className="mb-1 block">
                    {m.sl_note_filter_to_date()}
                </Label>
                <Input
                    type="date"
                    value={search.to_date ?? ''}
                    onChange={(e) =>
                        setSearch({
                            ...search,
                            to_date: e.target.value || undefined,
                            page: undefined,
                        })
                    }
                />
            </div>
        </>
    )

    return (
        <div className="space-y-4 p-4 md:p-6">
            <ProductLotNotePageHeader
                icon={<PackagePlus className="size-5 shrink-0" />}
                title={m.pll_pnk_title()}
                utilityActions={
                    <>
                        <RefreshButton
                            onClick={() => void refetch()}
                            loading={isFetching}
                        />
                        <ClearFilterButton
                            active={filterActive}
                            onClear={clearFilter}
                        />
                        <Button
                            variant="outline"
                            size="icon"
                            className="md:hidden"
                            onClick={() => setFilterDialogOpen(true)}
                        >
                            <Filter className="size-4" />
                        </Button>
                    </>
                }
                primaryActions={
                    <>
                        <Button asChild variant="outline">
                            <Link
                                to="/sales-warehouse/stock-in-notes/quick"
                                className="w-full"
                            >
                                <ScanBarcode className="size-4 mr-1" />
                                {m.warehouse_quick_stock_in()}
                            </Link>
                        </Button>
                        <Button asChild>
                            <Link
                                to="/sales-warehouse/stock-in-notes/new"
                                className="w-full"
                            >
                                <Plus className="size-4 mr-1" />
                                {m.pll_note_create_pnk()}
                            </Link>
                        </Button>
                    </>
                }
            />

            <div className="hidden md:grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                {renderFilters()}
            </div>

            <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <DialogContent className="max-w-none w-screen h-[100svh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6">
                    <DialogHeader>
                        <DialogTitle>{m.pll_pnk_title()}</DialogTitle>
                    </DialogHeader>
                    <div className="grid content-start gap-3 overflow-y-auto py-2">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="h-11 flex-1"
                            onClick={() => {
                                clearFilter()
                                setFilterDialogOpen(false)
                            }}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            className="h-11 flex-1"
                            onClick={() => setFilterDialogOpen(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_lots_loading()}
                    </div>
                ) : mobileNotes.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_note_empty()}
                    </div>
                ) : (
                    <>
                        {mobileNotes.map((n) => (
                            <NoteListCard
                                key={n.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/sales-warehouse/stock-in-notes/$id',
                                        params: { id: n.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/sales-warehouse/stock-in-notes/$id"
                                        params={{ id: n.id }}
                                        className={NOTE_CARD_CODE_CLASS}
                                    >
                                        {n.code}
                                    </Link>
                                }
                                statusBadge={
                                    <ProductLotStatusBadge status={n.status} />
                                }
                                reason={reasonLabel(n.reason)}
                                totalLots={n.items_count ?? 0}
                                createdBy={n.created_by_name ?? undefined}
                                createdAt={n.created_at}
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_note_col_code()}</TableHead>
                            <TableHead>{m.sl_note_col_status()}</TableHead>
                            <TableHead>{m.sl_note_col_reason()}</TableHead>
                            <TableHead className="text-center">
                                {m.sl_note_col_items()}
                            </TableHead>
                            <TableHead>{m.sl_note_col_created_by()}</TableHead>
                            <TableHead>{m.sl_note_col_created_at()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : notes.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_note_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            notes.map((n) => (
                                <TableRow
                                    key={n.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/sales-warehouse/stock-in-notes/$id',
                                            params: { id: n.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/sales-warehouse/stock-in-notes/$id"
                                            params={{ id: n.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {n.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <ProductLotStatusBadge
                                            status={n.status}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {reasonLabel(n.reason)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {n.items_count ?? 0}
                                    </TableCell>
                                    <TableCell>
                                        {n.created_by_name ?? '—'}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(n.created_at)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden md:flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / 50)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}

export function reasonLabel(reason: ProductLotInReason | string): string {
    switch (reason) {
        case 'from_packaging':
            return m.pll_in_reason_from_packaging()
        case 'from_customer_return':
            return m.pll_in_reason_from_customer_return()
        case 'from_order_cancel':
            return m.pll_in_reason_from_order_cancel()
        case 'adjustment':
            return m.pll_in_reason_adjustment()
        case 'from_manufacturing_partner':
            return m.pll_in_reason_from_manufacturing_partner()
        case 'from_stocktake_surplus':
            return m.pl_reason_from_stocktake_surplus()
        default:
            return reason
    }
}
